import * as yup from "yup";

import {
  ApprovalTypeEnum,
  ModelTypeEnum,
  type ApprovalModel,
  type Approver,
  type Branch,
  type CostCenter,
  type CustomerEmployee,
} from "~/application/types";

export interface ApprovalModelStateApprover extends Omit<Approver, "order"> {
  order?: number;
  cpf: string;
  approvalModelName: string;
}

export interface IApprovalModelDialogState {
  isAllBranches: boolean;
  branchName: string;
  costCenterName: string;
  approvalType: string;
  selfApprover: boolean;
  approvers: ApprovalModelStateApprover[];
  isAllBranchesClicked: boolean;
}

export interface ApprovalModelDialogProps {
  isNew?: boolean;
  defaultData?: ApprovalModel;
  approvalModels?: ApprovalModel[];
  isLoadingApprovalModels: boolean;
  fetchApprovalModels: (name: string) => Promise<ApprovalModel[]>;
  fetchApprovers: (
    name: string,
    options?: {
      headquartersAccess?: boolean;
      branchUuids?: string[]
    }
  ) => Promise<CustomerEmployee[]>;
  onCloseClick?: () => void;
  onSubmit: (item?: any) => Promise<ApprovalModel>;
  onCreateApprover: (item?: any) => void;
  onUpdateApprover: (item?: any) => void;
  onInactivateApprover: (item: string) => void;
  customerId: string;
  isIssuer?: boolean;
}

const ArrayTypeSchema = yup.array().of(
  yup.object().shape({
    uuid: yup.string().required(),
    name: yup.string().required(),
  })
);

export const approvalModelSchema = yup.object().shape({
  branchName: yup
    .object()
    .shape({
      uuid: yup.string(),
      name: yup.string(),
    })
    .nullable()
    .when("isAllBranches", {
      is: false,
      then: (schema) =>
        schema.shape({
          uuid: yup.string().required("O nome da filial é obrigatório"),
          name: yup.string().required(),
        }),
      otherwise: (schema) => schema.nullable(),
    }),
  costCenters: ArrayTypeSchema
    .when(["modelTypes", "approvalType"], {
      is: (modelTypes?: ModelTypeEnum[] , approvalType?: ApprovalTypeEnum) =>
        !(modelTypes || []).includes(ModelTypeEnum.BROKEN_POLICY) &&
        approvalType === ApprovalTypeEnum.COST_CENTER,
      then: (schema) => schema
        .required("Selecione ao menos um centro de custo")
        .min(1, "Selecione ao menos um centro de custo"),
    }),
  projects: ArrayTypeSchema
    .when(["modelTypes", "approvalType"], {
      is: (modelTypes?: ModelTypeEnum[], approvalType?: ApprovalTypeEnum) =>
        !(modelTypes || []).includes(ModelTypeEnum.BROKEN_POLICY) &&
        (approvalType === ApprovalTypeEnum.PROJECT || approvalType === ApprovalTypeEnum.PHASE),
      then: (schema) => schema
        .required("Selecione ao menos um projeto")
        .min(1, "Selecione ao menos um projeto"),
    }),
  phases: ArrayTypeSchema
    .when(["modelTypes", "approvalType"], {
      is: (modelTypes?: ModelTypeEnum[], approvalType?: ApprovalTypeEnum) =>
        !(modelTypes || []).includes(ModelTypeEnum.BROKEN_POLICY) &&
        approvalType === ApprovalTypeEnum.PHASE,
      then: (schema) => schema
        .required("Selecione ao menos uma fase")
        .min(1, "Selecione ao menos uma fase"),
    }),
  groups: ArrayTypeSchema
    .when(["modelTypes", "approvalType"], {
      is: (modelTypes?: ModelTypeEnum[], approvalType?: ApprovalTypeEnum) =>
        !(modelTypes || []).includes(ModelTypeEnum.BROKEN_POLICY) &&
        approvalType === ApprovalTypeEnum.GROUP,
      then: (schema) => schema
        .required("Selecione ao menos um grupo")
        .min(1, "Selecione ao menos um grupo"),
    }),
  groupApprovers: ArrayTypeSchema
    .when(["modelTypes", "approvalType"], {
      is: (modelTypes?: ModelTypeEnum[], approvalType?: ApprovalTypeEnum) =>
        !(modelTypes || []).includes(ModelTypeEnum.BROKEN_POLICY) &&
        approvalType === ApprovalTypeEnum.GROUP,
      then: (schema) => schema
        .required("Selecione ao menos um grupo de aprovadores")
        .min(1, "Selecione ao menos um grupo de aprovadores"),
    }),
  isAllBranches: yup.boolean().default(true),
  levels: yup
    .array()
    .of(
      yup.object().shape({
        approvers: yup
          .array()
          .of(
            yup.object().shape({
              uuid: yup.string().required("O aprovador é obrigatório"),
              name: yup.string().required(),
              selfApprover: yup.boolean().default(false),
            })
          )
          .min(1, "Selecione pelo menos um aprovador")
          .required(),
      })
    )
    .min(1, "Selecione pelo menos um nível")
    .required(),
  name: yup.string().required("O nome é obrigatório"),
  outPolicyApprovalModel: yup
    .object()
    .shape({
      uuid: yup.string(),
      name: yup.string(),
    })
    .nullable(),
  approvalType: yup
    .string()
    .oneOf([
      ApprovalTypeEnum.PHASE,
      ApprovalTypeEnum.COST_CENTER,
      ApprovalTypeEnum.PROJECT,
      ApprovalTypeEnum.GROUP,
    ])
    .required("O tipo de aprovação é obrigatório"),
  modelTypes: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([
          ModelTypeEnum.TRAVEL,
          ModelTypeEnum.ADVANCE,
          ModelTypeEnum.EXPENSE_REPORT,
          ModelTypeEnum.BROKEN_POLICY,
        ])
    )
    .min(1, "Selecione ao menos um tipo de modelo")
    .required("O tipo de modelo é obrigatório"),
});

export type ApprovalModelSchema = yup.InferType<typeof approvalModelSchema>;

export type ApproverSchema = {
  uuid: string;
  name: string;
  selfApprover: boolean;
};

export type LevelSchema = {
  approvers: ApproverSchema[];
};

export type OnSelectApproverParams = {
  approver: Approver;
  levelIndex: number;
};

export type ApprovalTypeSchema = {
  value: ApprovalTypeEnum;
  label: string;
};

export type ModelTypeSchema = {
  value: ModelTypeEnum;
  label: string;
};